/**
 * Saved profit projections API client — CRUD for named what-if scenarios.
 * Shared across everyone with Reporting access. Mirrors the pure-fetch service
 * pattern (relative /api/* URLs; types from @techgitter/contracts).
 */

import type {
  ProfitProjection,
  ProfitProjectionListResponse,
  ProfitProjectionInput,
} from '@techgitter/contracts';

const BASE = '/api/reporting/projections';

async function readJson<T>(response: Response, op: string): Promise<T> {
  if (!response.ok) {
    let detail = '';
    try {
      detail = `: ${(await response.text()).slice(0, 200)}`;
    } catch {
      /* ignore */
    }
    throw new Error(`${op} failed (${response.status} ${response.statusText})${detail}`);
  }
  return (await response.json()) as T;
}

export async function fetchProjections(signal?: AbortSignal): Promise<ProfitProjection[]> {
  const response = await fetch(BASE, { headers: { Accept: 'application/json' }, signal });
  const body = await readJson<ProfitProjectionListResponse>(response, 'Load projections');
  return body.items ?? [];
}

export async function createProjection(input: ProfitProjectionInput): Promise<ProfitProjection> {
  const response = await fetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  return readJson<ProfitProjection>(response, 'Create projection');
}

export async function updateProjection(
  id: string,
  input: ProfitProjectionInput,
): Promise<ProfitProjection> {
  const response = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  return readJson<ProfitProjection>(response, 'Update projection');
}

export async function deleteProjection(id: string): Promise<void> {
  const response = await fetch(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  await readJson<{ deleted: boolean }>(response, 'Delete projection');
}
